import React, { createContext, useContext, useState, useCallback, PropsWithChildren, useRef, useEffect, useMemo } from 'react';
import { AppContext } from 'Context/AppContext';
import useDebounce from '../hooks/useDebounce';
import { fetchCategoriesAndCategoryTypes, fetchCoSoldCategories, toggleCuration } from '../services/CategoryRelevancyServices';
import { toast } from 'react-toastify';

export type CategoryRelevancyContextType = {
    activeTab: TabType;
    masterCategories: Category[];
    masterCategoryTypes: CategoryType[];
    rowDensity: RowDensity;
    industryId: string | null | undefined;
    openMenu: "filter" | "topItems" | "settings" | undefined;
    showTabSelector?: boolean;

    readPermission: boolean | undefined;
    updatePermission: boolean | undefined;
    rawCategories: Category[] | undefined;
    rawCategoryTypes: CategoryType[];
    rawExpandedCategories: Set<string>;
    rawCoSoldCategories: CoSoldCategoryParent[];
    rawFilters: FilterOptions;
    topItems: TopItemsOption;
    customTopItems: number;
    rawSearchTerm: string;
    rawSortBy: SortOption;
    rawSortOrder: SortOrder;
    rawLoadingCategories: boolean;
    isRawCoSoldCategoryLoading: (categoryId: string) => boolean

    curatedCategories: Category[] | undefined;
    curatedExpandedCategories: Set<string>;
    curatedCoSoldCategories: CoSoldCategoryParent[];
    curatedFilters: FilterOptions;
    curatedSearchTerm: string;
    curatedSortBy: SortOption;
    curatedSortOrder: SortOrder;
    curatedLoadingCategories: boolean;
    isCuratedCoSoldCategoryLoading: (categoryId: string) => boolean

    setActiveTab: (tab: TabType) => void;
    handleRowDensityChange: (density: RowDensity) => void;
    setOpenMenu: React.Dispatch<React.SetStateAction<"filter" | "topItems" | "settings" | undefined>>;

    handleRawCategoryExpand: (categoryId: string) => Promise<void>;
    handleToggleCuration: (coSoldCategoryId: string, parentCategoryId: string, isCurated: boolean) => Promise<void>;
    handleRawSearch: (term: string) => void;
    handleRawFilterChange: (newFilters: FilterOptions) => void;
    handleTopItemsChange: (option: TopItemsOption, customValue?: number) => void;
    handleRawRemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    handleRawSort: (column: SortOption) => void;
    getRawCoSoldCategoriesForId: (categoryId: string) => CoSoldCategory[];

    handleCuratedCategoryExpand: (categoryId: string) => Promise<void>;
    handleCuratedSearch: (term: string) => void;
    handleCuratedFilterChange: (newFilters: FilterOptions) => void;
    handleCuratedRemoveFilter: (filterType: keyof FilterOptions | 'all', value?: string) => void;
    handleCuratedSort: (column: SortOption) => void;
    getCuratedCoSoldCategoriesForId: (categoryId: string) => CoSoldCategory[];
}

export const CategoryRelevancyContext = createContext<CategoryRelevancyContextType>({} as CategoryRelevancyContextType);

type CategoryRelevancyProviderProps = PropsWithChildren<{
    initialTab?: TabType;
    showTabSelector?: boolean;
}>;

export const CategoryRelevancyProvider = ({
    children,
    initialTab = 'raw',
    showTabSelector = true
}: CategoryRelevancyProviderProps) => {
    const [activeTab, setActiveTab] = useState<TabType>(initialTab);
    const [masterCategories, setMasterCategories] = useState<Category[]>([]);
    const [masterCategoryTypes, setMasterCategoryTypes] = useState<CategoryType[]>([]);
    const [rowDensity, setRowDensity] = useState<RowDensity>('normal');
    const [openMenu, setOpenMenu] = useState<'filter' | 'topItems' | 'settings' | undefined>(undefined);

    const [rawCategories, setRawCategories] = useState<Category[] | undefined>(undefined);
    const [rawCategoryTypes, setRawCategoryTypes] = useState<CategoryType[]>([]);
    const [rawExpandedCategories, setRawExpandedCategories] = useState<Set<string>>(new Set());
    const [rawCoSoldCategories, setRawCoSoldCategories] = useState<CoSoldCategoryParent[]>([]);
    const [rawSortBy, setRawSortBy] = useState<SortOption>('category_name');
    const [rawSortOrder, setRawSortOrder] = useState<SortOrder>(1);
    const [rawFilters, setRawFilters] = useState<FilterOptions>({
        categories: [],
        category_types: [],
        co_sold_categories: [],
        co_sold_category_types: [],
    });
    const [topItems, setTopItems] = useState<TopItemsOption>(25);
    const [customTopItems, setCustomTopItems] = useState<number>(25);
    const [rawSearchTerm, setRawSearchTerm] = useState<string>('');
    const [rawLoadingCategories, setRawLoadingCategories] = useState<boolean>(false);
    const [rawLoadingCoSoldCategoryIds, setRawLoadingCoSoldCategoryIds] = useState<string[]>([]);

    const [curatedCategories, setCuratedCategories] = useState<Category[] | undefined>(undefined);
    const [curatedExpandedCategories, setCuratedExpandedCategories] = useState<Set<string>>(new Set());
    const [curatedCoSoldCategories, setCuratedCoSoldCategories] = useState<CoSoldCategoryParent[]>([]);
    const [curatedSortBy, setCuratedSortBy] = useState<SortOption>('category_name');
    const [curatedSortOrder, setCuratedSortOrder] = useState<SortOrder>(1);
    const [curatedFilters, setCuratedFilters] = useState<FilterOptions>({
        categories: [],
        category_types: [],
        co_sold_categories: [],
        co_sold_category_types: [],
    });
    const [curatedSearchTerm, setCuratedSearchTerm] = useState<string>('');
    const [curatedLoadingCategories, setCuratedLoadingCategories] = useState<boolean>(false);
    const [curatedLoadingCoSoldCategoryIds, setCuratedLoadingCoSoldCategoryIds] = useState<string[]>([]);

    const masterListsLoaded = useRef(false);
    const rawInitialized = useRef(false);
    const curatedInitialized = useRef(false);

    const debouncedRawSearchTerm = useDebounce(rawSearchTerm, 500);
    const debouncedCuratedSearchTerm = useDebounce(curatedSearchTerm, 500);

    const rawFetchParamsRef = useRef(new Map<string, {
        filters: FilterOptions;
        topItems: TopItemsOption;
        customTopItems: number;
        sortBy: SortOption;
        sortOrder: SortOrder;
        searchTerm: string;
    }>());

    const curatedFetchParamsRef = useRef(new Map<string, {
        filters: FilterOptions;
        sortBy: SortOption;
        sortOrder: SortOrder;
        searchTerm: string;
    }>());

    const appContext = useContext(AppContext);
    const industryId = appContext && appContext?.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const channelKey = appContext?.channelKey;
    const readPermission = appContext.pm.checkPermission("gmscategoryrelevancy_read");
    const updatePermission = appContext.pm.checkPermission("gmscategoryrelevancy_update");

    const loadMasterLists = useCallback(async () => {
        if (!channelKey || masterListsLoaded.current || !readPermission) return;

        try {
            const masterData = await fetchCategoriesAndCategoryTypes({
                search: '',
                channelKey: channelKey,
                sort_by: 'category_name',
                sort_order: 1
            });

            if (masterData?.data) {
                setMasterCategories(masterData.data.categories);
                setMasterCategoryTypesUnique(masterData.data.category_types);
                masterListsLoaded.current = true;
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, [channelKey]);

    const setMasterCategoryTypesUnique = (categoryTypes: CategoryType[]) => {
        const uniqueMap = new Map<string, CategoryType>();

        categoryTypes.forEach(category => {
            uniqueMap.set(category.category_type_id, category);
        });

        setMasterCategoryTypes(Array.from(uniqueMap.values()));
    };

    const fetchRawCategories = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey || !readPermission) return;

        try {
            setRawLoadingCategories(true);

            const filtersToUse = explicitFilters || rawFilters;
            const sortColumn = explicitSortBy !== undefined ? explicitSortBy : rawSortBy;
            const sortDirection = explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder;
            const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm;

            const data = await fetchCategoriesAndCategoryTypes({
                search: searchTerm,
                channelKey: channelKey,
                sort_by: sortColumn,
                sort_order: sortDirection,
                filters: filtersToUse
            });

            setRawCategories(data?.data?.categories || []);
            setRawCategoryTypes(data?.data?.category_types || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setRawLoadingCategories(false);
        }
    }, [channelKey, rawFilters, rawSortBy, rawSortOrder, debouncedRawSearchTerm]);

    const shouldRawRefetchData = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const currentParams = {
            filters: explicitFilters || rawFilters,
            topItems,
            customTopItems,
            sortBy: explicitSortBy !== undefined ? explicitSortBy : rawSortBy,
            sortOrder: explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder,
            searchTerm: explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm
        };

        const prevParams = rawFetchParamsRef.current.get(categoryId);

        if (!prevParams) return true;

        return prevParams.topItems !== currentParams.topItems ||
            prevParams.customTopItems !== currentParams.customTopItems ||
            prevParams.sortBy !== currentParams.sortBy ||
            prevParams.sortOrder !== currentParams.sortOrder ||
            prevParams.searchTerm !== currentParams.searchTerm ||
            JSON.stringify(prevParams.filters) !== JSON.stringify(currentParams.filters);
    }, [rawFilters, topItems, customTopItems, rawSortBy, rawSortOrder, debouncedRawSearchTerm]);

    const updateRawFetchParams = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const filtersToUse = explicitFilters || rawFilters;
        const sortToStore = explicitSortBy !== undefined ? explicitSortBy : rawSortBy;
        const orderToStore = explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder;
        const searchTermToStore = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm;

        rawFetchParamsRef.current.set(categoryId, {
            filters: filtersToUse,
            topItems,
            customTopItems,
            sortBy: sortToStore,
            sortOrder: orderToStore,
            searchTerm: searchTermToStore
        });
    }, [rawFilters, topItems, customTopItems, rawSortBy, rawSortOrder, debouncedRawSearchTerm]);

    const addToRawLoadingCategories = useCallback((categoryId: string) => {
        setRawLoadingCoSoldCategoryIds(prev =>
            prev.includes(categoryId) ? prev : [...prev, categoryId]
        );
    }, []);

    const removeFromRawLoadingCategories = useCallback((categoryId: string) => {
        setRawLoadingCoSoldCategoryIds(prev =>
            prev.filter(id => id !== categoryId)
        );
    }, []);

    const isRawCoSoldCategoryLoading = useCallback((categoryId: string) => {
        return rawLoadingCoSoldCategoryIds.includes(categoryId);
    }, [rawLoadingCoSoldCategoryIds]);

    const addToCuratedLoadingCategories = useCallback((categoryId: string) => {
        setCuratedLoadingCoSoldCategoryIds(prev =>
            prev.includes(categoryId) ? prev : [...prev, categoryId]
        );
    }, []);

    const removeFromCuratedLoadingCategories = useCallback((categoryId: string) => {
        setCuratedLoadingCoSoldCategoryIds(prev =>
            prev.filter(id => id !== categoryId)
        );
    }, []);

    const isCuratedCoSoldCategoryLoading = useCallback((categoryId: string) => {
        return curatedLoadingCoSoldCategoryIds.includes(categoryId);
    }, [curatedLoadingCoSoldCategoryIds]);

    const fetchRawCoSoldCategories = useCallback(async (
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey) return null;

        try {
            addToRawLoadingCategories(categoryId);

            const filtersToUse = explicitFilters || rawFilters;
            const sortColumn = explicitSortBy !== undefined ? explicitSortBy : rawSortBy;
            const sortDirection = explicitSortOrder !== undefined ? explicitSortOrder : rawSortOrder;
            const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedRawSearchTerm;
            const limit = topItems === 'custom' ? customTopItems : topItems;

            const response = await fetchCoSoldCategories(categoryId, channelKey, {
                limit,
                filters: filtersToUse,
                is_curated: undefined,
                sort_by: sortColumn,
                sort_order: sortDirection,
                search: searchTerm
            });

            updateRawFetchParams(
                categoryId,
                filtersToUse,
                sortColumn,
                sortDirection,
                searchTerm
            );

            if (response?.data) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching raw co-sold categories:', error);
            return null;
        } finally {
            removeFromRawLoadingCategories(categoryId);
        }
    }, [channelKey, rawFilters, topItems, customTopItems, rawSortBy, rawSortOrder, updateRawFetchParams, debouncedRawSearchTerm]);

    const refreshRawExpandedCategories = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        forceRefresh: boolean = false,
        explicitSearchTerm?: string
    ) => {
        if (rawExpandedCategories.size === 0) return;

        // setRawLoadingCoSoldCategories(true);

        try {
            const categoriesToRefresh = forceRefresh
                ? Array.from(rawExpandedCategories)
                : Array.from(rawExpandedCategories).filter(categoryId =>
                    shouldRawRefetchData(
                        categoryId,
                        explicitFilters,
                        explicitSortBy,
                        explicitSortOrder,
                        explicitSearchTerm
                    )
                );

            if (categoriesToRefresh.length === 0) {
                return;
            } else {
                categoriesToRefresh.forEach((e) => addToRawLoadingCategories(e));
            }

            const promises = categoriesToRefresh.map(categoryId =>
                fetchRawCoSoldCategories(
                    categoryId,
                    explicitFilters,
                    explicitSortBy,
                    explicitSortOrder,
                    explicitSearchTerm
                )
            );

            const results = await Promise.all(promises);
            const validResults = results.filter(Boolean) as CoSoldCategoryParent[];

            if (validResults.length > 0) {
                setRawCoSoldCategories(prev => {
                    const refreshedIds = new Set(validResults.map(item => item.category_id));
                    const remainingCategories = prev.filter(item => !refreshedIds.has(item.category_id));
                    return [...remainingCategories, ...validResults];
                });
            }
        } catch (error) {
            console.error('Error refreshing co-sold categories', error);
        } finally {
            rawExpandedCategories.forEach((e) => removeFromRawLoadingCategories(e));
        }
    }, [rawExpandedCategories, shouldRawRefetchData, fetchRawCoSoldCategories]);

    const handleRawSort = useCallback((column: SortOption) => {
        const isSameColumn = column === rawSortBy;
        const newSortOrder = isSameColumn ? (rawSortOrder === 1 ? -1 : 1) : 1;

        if (column === 'category_name' || column === 'category_type_name') {
            fetchRawCategories(undefined, column, newSortOrder);
        }

        if (column !== 'category_name' && column !== 'category_type_name') {
            refreshRawExpandedCategories(undefined, column, newSortOrder, true);
        }

        setRawSortBy(column);
        setRawSortOrder(newSortOrder);
    }, [fetchRawCategories, refreshRawExpandedCategories, rawSortBy, rawSortOrder]);

    const handleRawCategoryExpand = useCallback(async (categoryId: string) => {
        if (rawExpandedCategories.has(categoryId)) {
            setRawCoSoldCategories(prev =>
                prev.filter(item => item.category_id !== categoryId)
            );
            const newExpandedCategories = new Set(rawExpandedCategories);
            newExpandedCategories.delete(categoryId);
            setRawExpandedCategories(newExpandedCategories);
            return;
        }

        const needsRefetch = shouldRawRefetchData(categoryId);
        const alreadyHasData = rawCoSoldCategories.some(cat => cat.category_id === categoryId);

        try {
            addToRawLoadingCategories(categoryId);

            if (needsRefetch || !alreadyHasData) {
                const categoryData = await fetchRawCoSoldCategories(categoryId);

                if (categoryData) {
                    setRawCoSoldCategories(prev => {
                        const exists = prev.some(item => item.category_id === categoryId);
                        if (exists) {
                            return prev.map(item =>
                                item.category_id === categoryId ? categoryData : item
                            );
                        } else {
                            return [...prev, categoryData];
                        }
                    });
                }
            }

            const newExpandedCategories = new Set(rawExpandedCategories);
            newExpandedCategories.add(categoryId);
            setRawExpandedCategories(newExpandedCategories);
        } catch (error) {
            console.error('Error expanding category:', error);
        } finally {
            removeFromRawLoadingCategories(categoryId);
        }
    }, [rawExpandedCategories, rawCoSoldCategories, shouldRawRefetchData, fetchRawCoSoldCategories]);

    const handleRawSearch = useCallback((term: string) => {
        setRawSearchTerm(term);
    }, []);

    const handleRawFilterChange = useCallback((newFilters: FilterOptions) => {
        const isCategoryFilterChanged =
            JSON.stringify(rawFilters.categories) !== JSON.stringify(newFilters.categories) ||
            JSON.stringify(rawFilters.category_types) !== JSON.stringify(newFilters.category_types);

        const isCoSoldFilterChanged =
            JSON.stringify(rawFilters.co_sold_categories) !== JSON.stringify(newFilters.co_sold_categories) ||
            JSON.stringify(rawFilters.co_sold_category_types) !== JSON.stringify(newFilters.co_sold_category_types) ||
            rawFilters.is_curated !== newFilters.is_curated;

        setRawFilters(newFilters);

        if (isCategoryFilterChanged || isCoSoldFilterChanged) {
            setRawLoadingCategories(true);

            fetchCategoriesAndCategoryTypes({
                search: debouncedRawSearchTerm,
                channelKey: channelKey,
                sort_by: rawSortBy,
                sort_order: rawSortOrder,
                filters: newFilters
            }).then(data => {
                if (data?.data) {
                    setRawCategories(data.data.categories);
                    setRawCategoryTypes(data.data.category_types);

                    const newCategoryIds = new Set(data.data.categories.map((cat: Category) => cat.category_id));
                    const validExpandedCategories = new Set(
                        Array.from(rawExpandedCategories)
                            .filter(categoryId => newCategoryIds.has(categoryId))
                    );

                    setRawExpandedCategories(validExpandedCategories);

                    setRawCoSoldCategories(prev =>
                        prev.filter(item => validExpandedCategories.has(item.category_id))
                    );

                    if (validExpandedCategories.size > 0) {
                        refreshRawExpandedCategories(newFilters);
                    }
                }
                setRawLoadingCategories(false);
            }).catch(error => {
                console.error('Error fetching categories with filters:', error);
                setRawLoadingCategories(false);
            });
        }
    }, [
        rawFilters,
        debouncedRawSearchTerm,
        channelKey,
        rawSortBy,
        rawSortOrder,
        rawExpandedCategories,
        refreshRawExpandedCategories
    ]);

    const handleRawRemoveFilter = useCallback((filterType: keyof FilterOptions | 'all', value?: string) => {
        let newFilters: FilterOptions;

        if (filterType === 'all') {
            newFilters = {
                categories: [],
                category_types: [],
                co_sold_categories: [],
                co_sold_category_types: [],
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (filterType === 'is_curated') {
            newFilters = {
                ...rawFilters,
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (value && Array.isArray(rawFilters[filterType])) {
            newFilters = { ...rawFilters };

            if (filterType === 'categories' ||
                filterType === 'category_types' ||
                filterType === 'co_sold_categories' ||
                filterType === 'co_sold_category_types') {

                if (filterType === 'categories' || filterType === 'co_sold_categories') {
                    newFilters[filterType] = rawFilters[filterType].filter((item: Category) =>
                        item.category_id !== value
                    );
                }

                if (filterType === 'category_types' || filterType === 'co_sold_category_types') {
                    newFilters[filterType] = rawFilters[filterType].filter((item: CategoryType) =>
                        item.category_type_id !== value
                    );
                }
            }
        } else {
            return;
        }

        handleRawFilterChange(newFilters);
    }, [rawFilters, handleRawFilterChange]);

    const handleTopItemsChange = useCallback((option: TopItemsOption, customValue?: number) => {
        setTopItems(option);
        if (option === 'custom' && customValue !== undefined) {
            setCustomTopItems(customValue);
        }

        if (rawExpandedCategories.size === 0) return;

        const limit = option === 'custom'
            ? (customValue !== undefined ? customValue : customTopItems)
            : option;


        const categoriesToRefresh = Array.from(rawExpandedCategories);
        categoriesToRefresh.forEach((e) => addToRawLoadingCategories(e));

        Promise.all(
            categoriesToRefresh.map(categoryId =>
                fetchCoSoldCategories(categoryId, channelKey, {
                    limit,
                    filters: rawFilters,
                    is_curated: undefined,
                    sort_by: rawSortBy,
                    sort_order: rawSortOrder,
                    search: debouncedRawSearchTerm
                })
            )
        )
            .then(responses => {
                const validResults = responses
                    .filter(response => response?.data)
                    .map(response => response.data);

                if (validResults.length > 0) {
                    setRawCoSoldCategories(prev => {
                        const refreshedIds = new Set(validResults.map(item => item.category_id));
                        return [
                            ...prev.filter(item => !refreshedIds.has(item.category_id)),
                            ...validResults
                        ];
                    });
                }

                const newCustomValue = option === 'custom' && customValue !== undefined
                    ? customValue
                    : customTopItems;

                categoriesToRefresh.forEach(categoryId => {
                    rawFetchParamsRef.current.set(categoryId, {
                        filters: rawFilters,
                        topItems: option,
                        customTopItems: newCustomValue,
                        sortBy: rawSortBy,
                        sortOrder: rawSortOrder,
                        searchTerm: debouncedRawSearchTerm
                    });
                });
            })
            .catch(error => {
                console.error('Error fetching categories for top items:', error);
            })
            .finally(() => {
                categoriesToRefresh.forEach((e) => removeFromRawLoadingCategories(e));
            });
    }, [
        rawExpandedCategories,
        channelKey,
        rawFilters,
        rawSortBy,
        rawSortOrder,
        customTopItems,
        debouncedRawSearchTerm
    ]);

    const fetchCuratedCategoriesData = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey) return;

        try {
            setCuratedLoadingCategories(true);

            const baseFilters = explicitFilters || curatedFilters;

            const filtersWithCurated = {
                ...baseFilters,
                is_curated: true
            };

            const sortByToUse = explicitSortBy || curatedSortBy;
            const sortOrderToUse = explicitSortOrder || curatedSortOrder;
            const searchTermToUse = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm;

            const response = await fetchCategoriesAndCategoryTypes({
                search: searchTermToUse,
                channelKey: channelKey,
                sort_by: sortByToUse,
                sort_order: sortOrderToUse,
                filters: filtersWithCurated
            });

            if (response?.data) {
                const formattedData: CoSoldCategoryParent[] = response.data.categories.map((category: Category) => ({
                    category_id: category.category_id,
                    category_name: category.category_name,
                    co_sold_categories: []
                }));

                setCuratedCategories(formattedData);
            } else {
                setCuratedCategories([]);
            }
        } catch (error) {
            console.error('Error fetching curated categories:', error);
        } finally {
            setCuratedLoadingCategories(false);
        }
    }, [channelKey, curatedFilters, curatedSortBy, curatedSortOrder, debouncedCuratedSearchTerm]);

    const updateCuratedFetchParams = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const filtersToUse = explicitFilters || curatedFilters;
        const sortToStore = explicitSortBy !== undefined ? explicitSortBy : curatedSortBy;
        const orderToStore = explicitSortOrder !== undefined ? explicitSortOrder : curatedSortOrder;
        const searchTermToStore = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm;

        curatedFetchParamsRef.current.set(categoryId, {
            filters: filtersToUse,
            sortBy: sortToStore,
            sortOrder: orderToStore,
            searchTerm: searchTermToStore
        });
    }, [curatedFilters, curatedSortBy, curatedSortOrder, debouncedCuratedSearchTerm]);

    const fetchCuratedCoSoldCategories = useCallback(async (
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        if (!channelKey) return null;

        try {
            addToCuratedLoadingCategories(categoryId);

            const baseFilters = explicitFilters || curatedFilters;

            const filtersWithCurated = {
                ...baseFilters,
                is_curated: true
            };

            const sortColumn = explicitSortBy !== undefined ? explicitSortBy : curatedSortBy;
            const sortDirection = explicitSortOrder !== undefined ? explicitSortOrder : curatedSortOrder;
            const searchTerm = explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm;

            const limit = masterCategories.length || 100;

            const response = await fetchCoSoldCategories(categoryId, channelKey, {
                limit,
                filters: filtersWithCurated,
                is_curated: true,
                sort_by: sortColumn,
                sort_order: sortDirection,
                search: searchTerm
            });

            updateCuratedFetchParams(
                categoryId,
                baseFilters,
                sortColumn,
                sortDirection,
                searchTerm
            );

            if (response?.data) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching curated co-sold categories:', error);
            return null;
        } finally {
            removeFromCuratedLoadingCategories(categoryId);
        }
    }, [channelKey, curatedFilters, curatedSortBy, curatedSortOrder, masterCategories.length, debouncedCuratedSearchTerm, updateCuratedFetchParams]);

    const shouldCuratedRefetchData = useCallback((
        categoryId: string,
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        explicitSearchTerm?: string
    ) => {
        const currentParams = {
            filters: explicitFilters || curatedFilters,
            sortBy: explicitSortBy !== undefined ? explicitSortBy : curatedSortBy,
            sortOrder: explicitSortOrder !== undefined ? explicitSortOrder : curatedSortOrder,
            searchTerm: explicitSearchTerm !== undefined ? explicitSearchTerm : debouncedCuratedSearchTerm
        };

        const prevParams = curatedFetchParamsRef.current.get(categoryId);

        if (!prevParams) return true;

        return prevParams.sortBy !== currentParams.sortBy ||
            prevParams.sortOrder !== currentParams.sortOrder ||
            prevParams.searchTerm !== currentParams.searchTerm ||
            JSON.stringify(prevParams.filters) !== JSON.stringify(currentParams.filters);
    }, [curatedFilters, curatedSortBy, curatedSortOrder, debouncedCuratedSearchTerm]);

    const refreshCuratedExpandedCategories = useCallback(async (
        explicitFilters?: FilterOptions,
        explicitSortBy?: SortOption,
        explicitSortOrder?: SortOrder,
        forceRefresh: boolean = false,
        explicitSearchTerm?: string
    ) => {
        if (curatedExpandedCategories.size === 0) return;

        try {
            const categoriesToRefresh = forceRefresh
                ? Array.from(curatedExpandedCategories)
                : Array.from(curatedExpandedCategories).filter(categoryId =>
                    shouldCuratedRefetchData(
                        categoryId,
                        explicitFilters,
                        explicitSortBy,
                        explicitSortOrder,
                        explicitSearchTerm
                    )
                );

            if (categoriesToRefresh.length === 0) {
                return;
            } else {
                categoriesToRefresh.forEach((e) => addToCuratedLoadingCategories(e));
            }

            const promises = categoriesToRefresh.map(categoryId =>
                fetchCuratedCoSoldCategories(
                    categoryId,
                    explicitFilters,
                    explicitSortBy,
                    explicitSortOrder,
                    explicitSearchTerm
                )
            );

            const results = await Promise.all(promises);
            const validResults = results.filter(Boolean) as CoSoldCategoryParent[];

            if (validResults.length > 0) {
                setCuratedCoSoldCategories(prev => {
                    const refreshedIds = new Set(validResults.map(item => item.category_id));
                    const remainingCategories = prev.filter(item => !refreshedIds.has(item.category_id));
                    return [...remainingCategories, ...validResults];
                });
            }
        } catch (error) {
            console.error('Error refreshing curated co-sold categories:', error);
        } finally {
            curatedExpandedCategories.forEach((e) => removeFromCuratedLoadingCategories(e));
        }
    }, [curatedExpandedCategories, shouldCuratedRefetchData, fetchCuratedCoSoldCategories]);

    const handleCuratedSort = useCallback((column: SortOption) => {
        const isSameColumn = column === curatedSortBy;
        const newSortOrder = isSameColumn ? (curatedSortOrder === 1 ? -1 : 1) : 1;

        if (column === 'category_name' || column === 'category_type_name') {
            fetchCuratedCategoriesData(undefined, column, newSortOrder);
        }

        if (column !== 'category_name' && column !== 'category_type_name') {
            refreshCuratedExpandedCategories(undefined, column, newSortOrder, true);
        }

        setCuratedSortBy(column);
        setCuratedSortOrder(newSortOrder);
    }, [fetchCuratedCategoriesData, refreshCuratedExpandedCategories, curatedSortBy, curatedSortOrder]);

    const handleCuratedCategoryExpand = useCallback(async (categoryId: string) => {
        if (curatedExpandedCategories.has(categoryId)) {
            setCuratedCoSoldCategories(prev =>
                prev.filter(item => item.category_id !== categoryId)
            );

            const newExpandedCategories = new Set(curatedExpandedCategories);
            newExpandedCategories.delete(categoryId);
            setCuratedExpandedCategories(newExpandedCategories);

            return;
        }

        const needsRefetch = shouldCuratedRefetchData(categoryId);
        const alreadyHasData = curatedCoSoldCategories.some(cat => cat.category_id === categoryId);

        try {

            if (needsRefetch || !alreadyHasData) {
                addToCuratedLoadingCategories(categoryId);
                const categoryData = await fetchCuratedCoSoldCategories(categoryId);

                if (categoryData) {
                    setCuratedCoSoldCategories(prev => {
                        const exists = prev.some(item => item.category_id === categoryId);
                        if (exists) {
                            return prev.map(item =>
                                item.category_id === categoryId ? categoryData : item
                            );
                        } else {
                            return [...prev, categoryData];
                        }
                    });
                }
            }

            const newExpandedCategories = new Set(curatedExpandedCategories);
            newExpandedCategories.add(categoryId);
            setCuratedExpandedCategories(newExpandedCategories);
        } catch (error) {
            console.error('Error expanding curated category:', error);
        } finally {
            removeFromCuratedLoadingCategories(categoryId);
        }
    }, [curatedExpandedCategories, curatedCoSoldCategories, shouldCuratedRefetchData, fetchCuratedCoSoldCategories]);

    const handleCuratedSearch = useCallback((term: string) => {
        setCuratedSearchTerm(term);
    }, []);

    const handleCuratedFilterChange = useCallback((newFilters: FilterOptions) => {
        setCuratedFilters(newFilters);
        fetchCuratedCategoriesData(newFilters);

        if (curatedExpandedCategories.size > 0) {
            refreshCuratedExpandedCategories(newFilters);
        }
    }, [fetchCuratedCategoriesData, refreshCuratedExpandedCategories, curatedExpandedCategories]);

    const handleCuratedRemoveFilter = useCallback((filterType: keyof FilterOptions | 'all', value?: string) => {
        let newFilters: FilterOptions;

        if (filterType === 'all') {
            newFilters = {
                categories: [],
                category_types: [],
                co_sold_categories: [],
                co_sold_category_types: [],
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (filterType === 'is_curated') {
            newFilters = {
                ...curatedFilters,
                is_curated: undefined,
                curatedSelections: []
            };
        } else if (value && Array.isArray(curatedFilters[filterType])) {
            newFilters = { ...curatedFilters };

            if (filterType === 'categories' ||
                filterType === 'category_types' ||
                filterType === 'co_sold_categories' ||
                filterType === 'co_sold_category_types') {

                if (filterType === 'categories' || filterType === 'co_sold_categories') {
                    newFilters[filterType] = curatedFilters[filterType].filter((item: Category) =>
                        item.category_id !== value
                    );
                }

                if (filterType === 'category_types' || filterType === 'co_sold_category_types') {
                    newFilters[filterType] = curatedFilters[filterType].filter((item: CategoryType) =>
                        item.category_type_id !== value
                    );
                }
            }
        } else {
            return;
        }

        handleCuratedFilterChange(newFilters);
    }, [curatedFilters, handleCuratedFilterChange]);

    const handleToggleCuration = useCallback(async (
        coSoldCategoryId: string,
        parentCategoryId: string,
        isCurated: boolean
    ) => {
        if (!channelKey) return;
        if (!updatePermission) { toast.error("Permission denied."); return }

        const updateCurationState = (newIsCurated: boolean) => {
            setRawCoSoldCategories(prev =>
                prev.map(category =>
                    category.category_id === parentCategoryId
                        ? {
                            ...category,
                            co_sold_categories: category.co_sold_categories.map(item =>
                                item.category_id === coSoldCategoryId
                                    ? { ...item, is_curated: newIsCurated }
                                    : item
                            )
                        }
                        : category
                )
            );
        };

        updateCurationState(isCurated);

        try {
            const response = await toggleCuration(channelKey, parentCategoryId, coSoldCategoryId, isCurated);

            if (response?.done !== true) {
                updateCurationState(!isCurated);
                toast.error("Failed to update curation status");
            }
            if (activeTab === 'curated') {
                fetchCuratedCategoriesData();
            }
        } catch (error) {
            console.error('Error toggling curation:', error);
            const errorMessage = error instanceof Error
                ? error.message
                : 'Failed to update curation status';

            toast.error(errorMessage);
            updateCurationState(!isCurated);
        }
    }, [activeTab, fetchCuratedCategoriesData, channelKey]);

    const handleRowDensityChange = useCallback((density: RowDensity) => {
        setRowDensity(density);
    }, []);

    const handleTabChange = useCallback((tab: TabType) => {
        setOpenMenu(undefined);

        if (tab === activeTab) return;

        setActiveTab(tab);

        if (tab === 'curated') {
            fetchCuratedCategoriesData();
            if (!curatedInitialized.current) {
                curatedInitialized.current = true;
            }

            if (curatedExpandedCategories.size > 0) {

                const expandedCategoryIds = Array.from(curatedExpandedCategories);
                curatedExpandedCategories.forEach((e) => addToCuratedLoadingCategories(e));
                Promise.all(
                    expandedCategoryIds.map(categoryId =>
                        fetchCuratedCoSoldCategories(categoryId)
                    )
                )
                    .then(results => {
                        const validResults = results.filter(Boolean) as CoSoldCategoryParent[];
                        if (validResults.length > 0) {
                            setCuratedCoSoldCategories(validResults);
                        }
                    })
                    .catch(error => {
                        console.error('Error loading curated co-sold categories:', error);
                    })
                    .finally(() => {
                        curatedExpandedCategories.forEach((e) => removeFromCuratedLoadingCategories(e));
                    });
            }
        }
        else {
            if (!rawInitialized.current) {
                fetchRawCategories();
                rawInitialized.current = true;
            }

            if (rawExpandedCategories.size > 0) {
                refreshRawExpandedCategories(undefined, undefined, undefined, true);
            }
        }
    }, [
        activeTab,
        fetchRawCategories,
        fetchCuratedCategoriesData,
        fetchCuratedCoSoldCategories,
        refreshRawExpandedCategories,
        curatedExpandedCategories,
        rawExpandedCategories
    ]);

    const getRawCoSoldCategoriesForId = useCallback(
        (categoryId: string): CoSoldCategory[] => {
            const parent = rawCoSoldCategories.find(item => item.category_id === categoryId);
            return parent?.co_sold_categories || [];
        },
        [rawCoSoldCategories]
    );

    const getCuratedCoSoldCategoriesForId = useCallback(
        (categoryId: string): CoSoldCategory[] => {
            const parent = curatedCoSoldCategories.find(item => item.category_id === categoryId);
            return parent?.co_sold_categories || [];
        },
        [curatedCoSoldCategories]
    );

    useEffect(() => {
        if (channelKey && !masterListsLoaded.current) {
            loadMasterLists().then(() => {
                if (activeTab === 'raw') {
                    if (!rawInitialized.current) {
                        fetchRawCategories();
                        rawInitialized.current = true;
                    }
                } else {
                    if (!curatedInitialized.current) {
                        fetchCuratedCategoriesData();
                        curatedInitialized.current = true;
                    }
                }
            });
        }
    }, [channelKey, activeTab, fetchRawCategories, fetchCuratedCategoriesData, loadMasterLists]);

    useEffect(() => {
        if (!channelKey || !masterListsLoaded.current) return;

        if (activeTab === 'raw' && !rawInitialized.current) {
            fetchRawCategories();
            rawInitialized.current = true;
        } else if (activeTab === 'curated' && !curatedInitialized.current) {
            fetchCuratedCategoriesData();
            curatedInitialized.current = true;
        }
    }, [channelKey, activeTab, fetchRawCategories, fetchCuratedCategoriesData, masterListsLoaded.current]);

    const prevRawSearchTermRef = useRef<string>('');

    const prevCuratedSearchTermRef = useRef<string>('');

    useEffect(() => {
        const searchTermChanged = prevRawSearchTermRef.current !== debouncedRawSearchTerm;

        if (activeTab === 'raw' && rawInitialized.current && searchTermChanged) {
            fetchRawCategories();

            if (rawExpandedCategories.size > 0) {
                refreshRawExpandedCategories(
                    undefined,
                    undefined,
                    undefined,
                    true,
                    debouncedRawSearchTerm
                );
            }

            prevRawSearchTermRef.current = debouncedRawSearchTerm;
        }
    }, [debouncedRawSearchTerm, fetchRawCategories, refreshRawExpandedCategories, activeTab, rawInitialized]);

    useEffect(() => {
        const searchTermChanged = prevCuratedSearchTermRef.current !== debouncedCuratedSearchTerm;

        if (activeTab === 'curated' && curatedInitialized.current && searchTermChanged) {
            fetchCuratedCategoriesData();

            if (curatedExpandedCategories.size > 0) {
                refreshCuratedExpandedCategories(
                    undefined,
                    undefined,
                    undefined,
                    true,
                    debouncedCuratedSearchTerm
                );
            }

            prevCuratedSearchTermRef.current = debouncedCuratedSearchTerm;
        }
    }, [debouncedCuratedSearchTerm, fetchCuratedCategoriesData, refreshCuratedExpandedCategories, activeTab, curatedInitialized]);

    const contextValue = useMemo(() => ({
        activeTab,
        masterCategories,
        masterCategoryTypes,
        rowDensity,
        industryId,
        openMenu,
        showTabSelector,
        rawCategories,
        rawCategoryTypes,
        rawExpandedCategories,
        rawCoSoldCategories,
        rawFilters,
        topItems,
        customTopItems,
        rawSearchTerm,
        rawSortBy,
        rawSortOrder,
        rawLoadingCategories,
        curatedCategories,
        curatedExpandedCategories,
        curatedCoSoldCategories,
        curatedFilters,
        curatedSearchTerm,
        curatedSortBy,
        curatedSortOrder,
        curatedLoadingCategories,
        isRawCoSoldCategoryLoading,
        isCuratedCoSoldCategoryLoading,
        setActiveTab: handleTabChange,
        handleRowDensityChange,
        setOpenMenu,
        handleToggleCuration,
        handleRawCategoryExpand,
        handleRawSearch,
        handleRawFilterChange,
        handleTopItemsChange,
        handleRawRemoveFilter,
        handleRawSort,
        getRawCoSoldCategoriesForId,
        handleCuratedCategoryExpand,
        handleCuratedSearch,
        handleCuratedFilterChange,
        handleCuratedRemoveFilter,
        handleCuratedSort,
        getCuratedCoSoldCategoriesForId,
        readPermission,
        updatePermission
    }), [
        activeTab, masterCategories, masterCategoryTypes, rowDensity, industryId, openMenu, showTabSelector,
        rawCategories, rawCategoryTypes, rawExpandedCategories, rawCoSoldCategories, rawFilters,
        topItems, customTopItems, rawSearchTerm, rawSortBy, rawSortOrder, rawLoadingCategories,
        curatedCategories, curatedExpandedCategories, curatedCoSoldCategories,
        curatedFilters, curatedSearchTerm, curatedSortBy, curatedSortOrder, curatedLoadingCategories,
        isRawCoSoldCategoryLoading, isCuratedCoSoldCategoryLoading, handleTabChange, handleRowDensityChange, handleToggleCuration,
        handleRawCategoryExpand, handleRawSearch, handleRawFilterChange, handleTopItemsChange,
        handleRawRemoveFilter, handleRawSort, getRawCoSoldCategoriesForId, handleCuratedCategoryExpand,
        handleCuratedSearch, handleCuratedFilterChange, handleCuratedRemoveFilter, handleCuratedSort,
        getCuratedCoSoldCategoriesForId, readPermission, updatePermission
    ]);

    return (
        <CategoryRelevancyContext.Provider value={contextValue}>
            {children}
        </CategoryRelevancyContext.Provider>
    );
};